import { datalayerPush } from '@/features/shared/HOCs/withGTM';
import { useDataLayerSearchEvents } from '@/features/shared/Hooks/data-layer';
import { MuiButton, MuiMenu, MuiMenuItem, MuiTypography } from '@/features/shared/material-ui';
import { Theme } from '@/utils/constants';
import { getNextId } from '@/utils/helpers';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import React, { memo, MouseEvent, useContext, useEffect, useRef, useState } from 'react';
import ElasticComponentContext from '../ElasticComponentContext';
import useStyles from './SortBy.styles';

const SortBy = ({
  theme = Theme.light,
  className: overrideClassName = '',
}: {
  theme?: ThemeType;
  className?: string;
}) => {
  const idElasticSortBy = useRef(getNextId('elastic-sort-by-'));
  const classes = useStyles({});
  const {
    queryParams,
    updateQueryParams,
    sortOptions,
    labels = {},
  } = useContext(ElasticComponentContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sort, setSort] = useState(sortOptions.find((item) => item.key === queryParams.sortBy));
  const containerRef = useRef<HTMLDivElement>(null);
  const { triggerSearchEventDataLayer } = useDataLayerSearchEvents();

  useEffect(() => {
    setSort(sortOptions.find((item) => item.key === queryParams.sortBy));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.sortBy]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSort = (option: { key: string; value: string }) => {
    setSort(option);
    const variantName = (
      containerRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement
    )?.dataset?.datalayerVariant;

    datalayerPush({
      compRef: containerRef,
      event: 'gtm:custom:sort',
      args: {
        params: { sortOption: option.key, variantName },
      },
    });

    updateQueryParams('sortBy', option.key);
    triggerSearchEventDataLayer();
    handleClose();
  };

  const rootClassName = clsx(classes.root, `${theme}-theme`, overrideClassName);

  return (
    <div className={rootClassName} ref={containerRef}>
      <MuiTypography variant="overline" className={classes.label}>
        {labels.sort_by || 'SORT BY'}
      </MuiTypography>
      <MuiButton
        aria-controls={idElasticSortBy.current}
        aria-haspopup="true"
        className={classes.sortByButton}
        onClick={handleClick}
        variant="text"
        endIcon={<ExpandMoreIcon className={classes.menuIcon} />}
        data-datalayer-ignore-cta
      >
        <MuiTypography variant="overline" className={classes.mobileLabel}>
          {labels.sort_by || 'SORT BY'}
        </MuiTypography>
        <span className={classes.value}>{sort?.value}</span>
      </MuiButton>
      <MuiMenu
        id={idElasticSortBy.current}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {sortOptions.map((item: any) => (
          <MuiMenuItem selected={item.key === sort} key={item.key} onClick={() => handleSort(item)}>
            {item.value}
          </MuiMenuItem>
        ))}
      </MuiMenu>
    </div>
  );
};
export default memo(SortBy);
